import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material'
import { PieChart } from '@mui/x-charts/PieChart'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectStatusTidsDrillDownMap,
  selectTaskCompletionMetrics,
  StatusTidsDrillDownMap,
} from '../../../../redux/reports/task/assignedTeamMemberPerTask/selector'
import {
  setTaskStatusFilter,
  setTidsPracticeFilter,
  setOperationalManagerNameFilter,
  setFunctionalAreaFilter,
  setSupervisorNameFilter,
} from '../../../../redux/reports/task/assignedTeamMemberPerTask/slice'
import { AppDispatch, RootState } from '../../../../redux/store'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'

// ─── Colors ──────────────────────────────────────────────────────────────────

// Fixed colors for the status layer
const STATUS_COLORS: Record<string, string> = {
  Completed: '#59A14F',
  'In Progress': '#4E79A7',
  'Not Started': '#BAB0AC',
  Overdue: '#E15759',
}

const DRILL_COLORS = [
  '#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F',
  '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC',
  '#54A0FF', '#FF6B6B', '#48DBFB', '#1DD1A1', '#FECA57',
  '#5F27CD', '#00D2D3', '#FF9F43', '#C8D6E5', '#8395A7',
]

// ─── Legend Item ─────────────────────────────────────────────────────────────

interface LegendItemProps {
  color: string
  label: string
  count: number
  percentage?: number
  onClick?: () => void
  clickable?: boolean
}

const LegendItem: React.FC<LegendItemProps> = ({ color, label, count, percentage, onClick, clickable }) => (
  <Box
    display='flex'
    alignItems='center'
    gap={1}
    mb={0.75}
    onClick={onClick}
    sx={
      clickable
        ? { cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { backgroundColor: 'action.hover' } }
        : undefined
    }
  >
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: fontSizes.size13, fontWeight: fontWeights[500], flex: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: fontSizes.size13, color: 'text.secondary', minWidth: 80, textAlign: 'right', pl: 2 }}>
      {count}{percentage !== undefined ? ` (${percentage}%)` : ''}
    </Typography>
  </Box>
)

// ─── Layer type ───────────────────────────────────────────────────────────────

type Layer = 'status' | 'tidsPractice' | 'operationalManager' | 'functionalArea' | 'supervisor'

// ─── Main Component ──────────────────────────────────────────────────────────

const SupervisorDrillDownChart: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const metrics = useSelector(selectTaskCompletionMetrics)
  const drillDown: StatusTidsDrillDownMap = useSelector(selectStatusTidsDrillDownMap)

  const [layer, setLayer] = useState<Layer>('status')
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
  const [selectedTids, setSelectedTids] = useState<string | null>(null)
  const [selectedOpManager, setSelectedOpManager] = useState<string | null>(null)
  const [selectedFa, setSelectedFa] = useState<string | null>(null)

  // Read Redux filter state to sync local chart state when chips are deleted
  const reduxStatusFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.taskStatus,
  )
  const reduxTidsFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.tidsPractice,
  )
  const reduxOpManagerFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.operationalManagerName,
  )
  const reduxFaFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.functionalArea,
  )
  const reduxSupervisorFilter = useSelector(
    (state: RootState) => state.assignedTeamMemberPerTask.filters.supervisorName,
  )

  // Sync chart layer with Redux filter changes (e.g. chip deletes)
  useEffect(() => {
    if (!reduxStatusFilter) {
      setSelectedStatus(null)
      setSelectedTids(null)
      setSelectedOpManager(null)
      setSelectedFa(null)
      setLayer('status')
    } else if (!reduxTidsFilter) {
      setSelectedTids(null)
      setSelectedOpManager(null)
      setSelectedFa(null)
      setLayer('tidsPractice')
    } else if (!reduxOpManagerFilter) {
      setSelectedTids(reduxTidsFilter)
      setSelectedOpManager(null)
      setSelectedFa(null)
      setLayer('operationalManager')
    } else if (!reduxFaFilter) {
      setSelectedOpManager(reduxOpManagerFilter)
      setSelectedFa(null)
      setLayer('functionalArea')
    } else if (!reduxSupervisorFilter) {
      setSelectedFa(reduxFaFilter)
      setLayer('supervisor')
    }
  }, [reduxStatusFilter, reduxTidsFilter, reduxOpManagerFilter, reduxFaFilter, reduxSupervisorFilter])

  const { totalAssigned } = metrics

  if (totalAssigned === 0) return null

  // helper: sum all leaves of a TidsDrillDownMap sub-tree (tids → opManager → fa → supervisor → count)
  const sumTids = (tidsMap: Record<string, Record<string, Record<string, Record<string, number>>>>): number =>
    Object.values(tidsMap).reduce(
      (s, opMap) =>
        s + Object.values(opMap).reduce(
          (s2, faMap) =>
            s2 + Object.values(faMap).reduce(
              (s3, supMap) => s3 + Object.values(supMap).reduce((s4, c) => s4 + c, 0),
              0,
            ),
          0,
        ),
      0,
    )

  const sumOpManager = (opMap: Record<string, Record<string, Record<string, number>>>): number =>
    Object.values(opMap).reduce(
      (s, faMap) =>
        s + Object.values(faMap).reduce(
          (s2, supMap) => s2 + Object.values(supMap).reduce((s3, c) => s3 + c, 0),
          0,
        ),
      0,
    )

  const sumFa = (faMap: Record<string, Record<string, number>>): number =>
    Object.values(faMap).reduce(
      (s, supMap) => s + Object.values(supMap).reduce((s2, c) => s2 + c, 0),
      0,
    )

  // ── Layer 0: Status ───────────────────────────────────────────────────────
  const STATUS_ORDER = ['Completed', 'In Progress', 'Not Started', 'Overdue']
  const statusEntries = STATUS_ORDER.map((name) => ({
    name,
    count: drillDown[name] ? sumTids(drillDown[name]) : 0,
  })).filter((e) => e.count > 0)

  const statusTotal = statusEntries.reduce((s, e) => s + e.count, 0)
  const statusPieData = statusEntries.map((e) => ({
    id: e.name,
    value: e.count,
    label: e.name,
    color: STATUS_COLORS[e.name] ?? '#BAB0AC',
  }))

  // ── Layer 1: TIDS Practice (within selected status) ───────────────────────
  const tidsPracticeEntries =
    selectedStatus && drillDown[selectedStatus]
      ? Object.entries(drillDown[selectedStatus])
          .map(([name, opMap]) => ({ name, count: sumOpManager(opMap) }))
          .sort((a, b) => b.count - a.count)
      : []

  const tidsTotal = tidsPracticeEntries.reduce((s, e) => s + e.count, 0)
  const tidsPieData = tidsPracticeEntries.map((e, idx) => ({
    id: idx,
    value: e.count,
    label: e.name,
    color: DRILL_COLORS[idx % DRILL_COLORS.length],
  }))

  // ── Layer 2: Operational Manager ─────────────────────────────────────────
  const opManagerEntries =
    selectedStatus && selectedTids && drillDown[selectedStatus]?.[selectedTids]
      ? Object.entries(drillDown[selectedStatus][selectedTids])
          .map(([name, faMap]) => ({ name, count: sumFa(faMap) }))
          .sort((a, b) => b.count - a.count)
      : []

  const opManagerTotal = opManagerEntries.reduce((s, e) => s + e.count, 0)
  const opManagerPieData = opManagerEntries.map((e, idx) => ({
    id: idx,
    value: e.count,
    label: e.name,
    color: DRILL_COLORS[idx % DRILL_COLORS.length],
  }))

  // ── Layer 3: Functional Area ──────────────────────────────────────────────
  const faEntries =
    selectedStatus && selectedTids && selectedOpManager &&
    drillDown[selectedStatus]?.[selectedTids]?.[selectedOpManager]
      ? Object.entries(drillDown[selectedStatus][selectedTids][selectedOpManager])
          .map(([name, supMap]) => ({
            name,
            count: Object.values(supMap).reduce((s, c) => s + c, 0),
          }))
          .sort((a, b) => b.count - a.count)
      : []

  const faTotal = faEntries.reduce((s, e) => s + e.count, 0)
  const faPieData = faEntries.map((e, idx) => ({
    id: idx,
    value: e.count,
    label: e.name,
    color: DRILL_COLORS[idx % DRILL_COLORS.length],
  }))

  // ── Layer 4: Supervisor ───────────────────────────────────────────────────
  const supervisorEntries =
    selectedStatus && selectedTids && selectedOpManager && selectedFa &&
    drillDown[selectedStatus]?.[selectedTids]?.[selectedOpManager]?.[selectedFa]
      ? Object.entries(drillDown[selectedStatus][selectedTids][selectedOpManager][selectedFa])
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
      : []

  const supervisorTotal = supervisorEntries.reduce((s, e) => s + e.count, 0)
  const supervisorPieData = supervisorEntries.map((e, idx) => ({
    id: idx,
    value: e.count,
    label: e.name,
    color: DRILL_COLORS[idx % DRILL_COLORS.length],
  }))

  // ── Current pie data ──────────────────────────────────────────────────────
  const currentPieData =
    layer === 'status'
      ? statusPieData
      : layer === 'tidsPractice'
      ? tidsPieData
      : layer === 'operationalManager'
      ? opManagerPieData
      : layer === 'functionalArea'
      ? faPieData
      : supervisorPieData

  const currentTotal =
    layer === 'status'
      ? statusTotal
      : layer === 'tidsPractice'
      ? tidsTotal
      : layer === 'operationalManager'
      ? opManagerTotal
      : layer === 'functionalArea'
      ? faTotal
      : supervisorTotal

  // ── Click handlers ────────────────────────────────────────────────────────
  const handleSliceClick = (_event: React.MouseEvent, _id: any, item: any) => {
    const label = currentPieData[item.dataIndex]?.label
    if (!label) return
    handleLegendClick(label)
  }

  const handleLegendClick = (label: string) => {
    if (layer === 'status') {
      setSelectedStatus(label)
      setSelectedTids(null)
      setSelectedOpManager(null)
      setSelectedFa(null)
      setLayer('tidsPractice')
      // map display label back to filter value
      const statusFilterMap: Record<string, string> = {
        Completed: 'completed',
        'In Progress': 'inprogress',
        'Not Started': 'notstarted',
        Overdue: 'overdue',
      }
      dispatch(setTaskStatusFilter(statusFilterMap[label] ?? label))
      dispatch(setTidsPracticeFilter(undefined))
      dispatch(setOperationalManagerNameFilter(undefined))
      dispatch(setFunctionalAreaFilter(undefined))
      dispatch(setSupervisorNameFilter(undefined))
    } else if (layer === 'tidsPractice') {
      setSelectedTids(label)
      setSelectedOpManager(null)
      setSelectedFa(null)
      setLayer('operationalManager')
      dispatch(setTidsPracticeFilter(label))
      dispatch(setOperationalManagerNameFilter(undefined))
      dispatch(setFunctionalAreaFilter(undefined))
      dispatch(setSupervisorNameFilter(undefined))
    } else if (layer === 'operationalManager') {
      setSelectedOpManager(label)
      setSelectedFa(null)
      setLayer('functionalArea')
      dispatch(setOperationalManagerNameFilter(label))
      dispatch(setFunctionalAreaFilter(undefined))
      dispatch(setSupervisorNameFilter(undefined))
    } else if (layer === 'functionalArea') {
      setSelectedFa(label)
      setLayer('supervisor')
      dispatch(setFunctionalAreaFilter(label))
      dispatch(setSupervisorNameFilter(undefined))
    } else if (layer === 'supervisor') {
      dispatch(setSupervisorNameFilter(label))
    }
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 3 }}>
        {/* ── Pie Chart ── */}
        <Box sx={{ flexShrink: 0 }}>
          <Tooltip title='Click a slice to drill down' placement='top' arrow>
            <Box sx={{ cursor: 'pointer' }}>
              <PieChart
                series={[{ data: currentPieData, innerRadius: 70, outerRadius: 155, paddingAngle: 2, cornerRadius: 4, highlightScope: { fade: 'global', highlight: 'item' } }]}
                width={340}
                height={340}
                hideLegend
                onItemClick={handleSliceClick}
              />
            </Box>
          </Tooltip>
        </Box>

        {/* ── Legend ── */}
        <Box sx={{ width: 300, maxHeight: 300, overflowY: 'auto' }}>
          {currentPieData.map((item) => (
            <LegendItem
              key={item.label}
              color={item.color}
              label={item.label}
              count={item.value}
              percentage={currentTotal > 0 ? parseFloat(((item.value / currentTotal) * 100).toFixed(1)) : 0}
              clickable
              onClick={() => handleLegendClick(item.label)}
            />
          ))}
          <Divider sx={{ my: 1 }} />
          <Box display='flex' alignItems='center' gap={1}>
            <Typography sx={{ fontSize: fontSizes.size13, fontWeight: fontWeights[700], flex: 1 }}>
              Total
            </Typography>
            <Typography sx={{ fontSize: fontSizes.size13, fontWeight: fontWeights[700], minWidth: 56, textAlign: 'right' }}>
              {currentTotal}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SupervisorDrillDownChart
